import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getAccessToken, api } from './client.js';

/**
 * Live updates from the control plane.
 *
 * `fetch` with a streaming body rather than `EventSource`, because EventSource
 * cannot send an Authorization header -- which is why WebSocket and EventSource
 * auth so often ends up as a token in a query string. A plain fetch carries the
 * bearer token the same way every other request does.
 *
 * Events are NUDGES. Nothing here carries a registration; it says "something
 * changed for this tenant" and the caller refetches through the normal API. One
 * code path reads data, one set of permissions governs it.
 *
 * The chain end to end: FreeSWITCH raises sofia::register, the agent pushes
 * within about a second, the API fans out, the browser refetches. Roughly two
 * seconds from handset to screen, against the thirty-second sweep it replaces.
 */

export type RealtimeState = 'connecting' | 'live' | 'offline';

const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 30_000;

interface PresenceEntry {
  tenant_id: string; realm: string; number: string; registered: boolean;
  contact_ip: string | null; contact_port: number | null;
  transport: string | null; user_agent: string | null; ping_status: string | null;
  expires_at: string | null; last_seen_at: string | null;
  offline_since: string | null; first_seen_at: string | null; snapshot_at: string | null;
}

/**
 * Merges fresh presence into whatever the cache already holds.
 *
 * Deliberately a no-op on anything not already cached: this is an update, not a
 * fetch. A page that has never loaded its extensions has nothing to recolour,
 * and making this populate the cache would fire off list-shaped writes from a
 * presence-shaped response.
 */
async function applyPresence(qc: QueryClient, tenantId: string | null): Promise<void> {
  let entries: PresenceEntry[];
  try {
    const res = await api.get<{ available: boolean; data: PresenceEntry[] }>(
      tenantId ? `/v1/live/presence?tenant_id=${encodeURIComponent(tenantId)}` : '/v1/live/presence');
    // available:false means the API has no node beside it. Leaving the cache
    // untouched shows the last known state, which beats blanking every dot.
    if (!res.available) return;
    entries = res.data;
  } catch {
    // A failed presence fetch must not disturb the page. The next event, or the
    // agent's 30-second sweep, tries again.
    return;
  }

  const byNumber = new Map(entries.map((e) => [`${e.tenant_id}|${e.number}`, e]));

  /** The registration sub-object the extension DTO carries. */
  const toRegistration = (e: PresenceEntry | undefined) => (e === undefined ? undefined : {
    registered: e.registered,
    contact: null as string | null,
    contact_ip: e.contact_ip,
    contact_port: e.contact_port,
    transport: e.transport,
    user_agent: e.user_agent,
    ping_status: e.ping_status,
    expires_at: e.expires_at,
    last_seen_at: e.last_seen_at,
    offline_since: e.offline_since,
    first_seen_at: e.first_seen_at,
    snapshot_at: e.snapshot_at,
  });

  // Tenant-scoped list: { data: Extension[] }
  qc.setQueriesData<{ data: Array<Record<string, unknown>> }>(
    { queryKey: ['extensions'] },
    (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((x) => {
          const e = byNumber.get(`${String(x['tenant_id'])}|${String(x['number'])}`);
          const reg = toRegistration(e);
          // `contact` is not carried by the presence endpoint; keep whatever the
          // last full fetch had rather than blanking a field we did not ask for.
          return reg
            ? { ...x, registration: { ...reg, contact:
                (x['registration'] as { contact?: string | null } | undefined)?.contact ?? null } }
            : x;
        }),
      };
    });

  // A single extension: the detail page.
  qc.setQueriesData<Record<string, unknown>>(
    { queryKey: ['extension'] },
    (old) => {
      if (!old || typeof old['number'] !== 'string') return old;
      const e = byNumber.get(`${String(old['tenant_id'])}|${String(old['number'])}`);
      const reg = toRegistration(e);
      return reg
        ? { ...old, registration: { ...reg, contact:
            (old['registration'] as { contact?: string | null } | undefined)?.contact ?? null } }
        : old;
    });

  // The operator's cross-tenant view, which is flat rather than nested.
  qc.setQueriesData<{ data: Array<Record<string, unknown>> }>(
    { queryKey: ['live-extensions'] },
    (old) => {
      if (!old?.data) return old;
      const merged: Array<Record<string, unknown>> = old.data.map((x) => {
        const e = byNumber.get(`${String(x['tenant_id'])}|${String(x['number'])}`);
        if (!e) return { ...x, registered: false };
        return {
          ...x,
          registered: e.registered,
          contact_ip: e.contact_ip, contact_port: e.contact_port,
          transport: e.transport, user_agent: e.user_agent,
          last_seen_at: e.last_seen_at, offline_since: e.offline_since,
          ever_seen: true,
        };
      });
      // Same order the server applies, so a row does not jump only because the
      // update arrived by a different route than the last one.
      merged.sort((a, b) =>
        Number(b['registered']) - Number(a['registered'])
        || String(a['tenant_name']).localeCompare(String(b['tenant_name']))
        || String(a['number']).localeCompare(String(b['number']), undefined, { numeric: true }));
      return { ...old, data: merged };
    });
}

/**
 * `platform` watches EVERY tenant on one connection.
 *
 * The alternative -- a stream per tenant -- means an operator with thirty
 * accounts holds thirty connections and has to reopen the set whenever one is
 * created. The API refuses the platform channel to anyone whose scope is not
 * actually the whole platform.
 */
export interface ExtensionLive {
  user: string; domain: string;
  state: 'idle' | 'ringing' | 'on_call';
  since: number;
  direction: 'inbound' | 'outbound' | 'internal' | null;
  peer: string | null;
}
export interface QueueLive {
  queue: string; name: string; domain: string; waiting: number;
  agents_total: number; agents_available: number; agents_on_call: number;
  calls_answered: number; longest_wait_sec: number;
}

export function useRealtime(
  tenantId: string | null,
  opts: {
    platform?: boolean;
    /**
     * Call state arrives as a PAYLOAD, not a nudge.
     *
     * Registration changes are rare enough that refetching is simplest, but a
     * bridged call fires several transitions in a second and refetching each
     * would turn one call into six requests. This state also has no
     * permissions of its own beyond the tenant it was published to.
     */
    onTelephony?: (data: { extensions: ExtensionLive[]; queues: QueueLive[] }) => void;
  } = {},
): RealtimeState {
  const qc = useQueryClient();
  const [state, setState] = useState<RealtimeState>('connecting');
  const platform = opts.platform === true;
  // Held in a ref so a new callback identity does not tear down the
  // connection: the stream should outlive a parent re-render.
  const onTelephonyRef = useRef(opts.onTelephony);
  onTelephonyRef.current = opts.onTelephony;

  useEffect(() => {
    if (!tenantId && !platform) return;
    let stopped = false;
    let controller: AbortController | null = null;
    let retry = RETRY_MIN_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = async (): Promise<void> => {
      if (stopped) return;
      controller = new AbortController();
      try {
        const url = platform
          ? '/api/v1/realtime'
          : `/api/v1/realtime?tenant_id=${encodeURIComponent(tenantId!)}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`realtime ${res.status}`);

        setState('live');
        retry = RETRY_MIN_MS;

        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done || stopped) break;
          buffer += value;

          // SSE frames are separated by a blank line. Anything short of one is
          // a partial frame and must stay in the buffer.
          let split: number;
          while ((split = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            if (frame.startsWith(':')) continue;   // keepalive comment

            const event = /^event:\s*(.+)$/m.exec(frame)?.[1]?.trim();
            if (event === 'registrations') {
              /**
               * Fetch presence, not the whole extension list.
               *
               * This used to invalidate, which meant every registration cost a
               * 1128 ms refetch of forty fields per extension in order to
               * recolour one dot -- and the other thirty-nine were already
               * correct in the cache. Presence now lives on the node and has
               * its own endpoint that costs single-digit milliseconds.
               *
               * Still not patched from the EVENT payload, which is what the
               * previous note was guarding against: the event carries no data,
               * and presence is read from one endpoint with one permission
               * governing it. The extension's own fields are never touched
               * here, so there is still exactly one writer for each of them.
               */
              void applyPresence(qc, platform ? null : tenantId);
            } else if (event === 'telephony' && onTelephonyRef.current) {
              const data = /^data:\s*(.+)$/m.exec(frame)?.[1];
              if (data) {
                try { onTelephonyRef.current(JSON.parse(data)); }
                catch { /* a frame we cannot parse is one frame, not a failure */ }
              }
            }
          }
        }
      } catch {
        // Aborts, network drops and 5xx all land here and are all the same
        // thing: reconnect. A failed stream must never surface as an error --
        // the page is still correct, just no longer live.
      }
      if (stopped) return;
      setState('offline');
      timer = setTimeout(() => void connect(), retry);
      retry = Math.min(retry * 2, RETRY_MAX_MS);
    };

    void connect();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
    };
  }, [tenantId, platform, qc]);

  return state;
}
